import { priorYearKey, yearOfKey } from "./dates";
import { joinKey } from "./engine";
import type {
  PivotCellValues,
  PivotConfig,
  PivotResult,
  PivotRowNode,
  PivotShowAs,
  PivotValue,
} from "./types";
import { TOTAL_KEY } from "./types";

type Cells = Record<string, PivotCellValues>;

const ratio = (part: number | null, whole: number | null | undefined) =>
  part === null || !whole ? null : (part / whole) * 100;

const leafKeys = (result: PivotResult) =>
  result.columnKeys.filter((column) => column.kind === "leaf");

const acrossColumns = (
  cells: Cells,
  result: PivotResult,
  value: PivotValue,
  showAs: PivotShowAs,
) => {
  const leaves = leafKeys(result);
  const out: Record<string, number | null> = {};
  let running = 0;
  let year = "";
  leaves.forEach((column, i) => {
    const current = cells[column.key]?.[value.id] ?? null;
    const previous =
      i > 0 ? (cells[leaves[i - 1].key]?.[value.id] ?? null) : null;
    switch (showAs) {
      case "diffFromPrevious":
        out[column.key] =
          i === 0 || current === null || previous === null
            ? null
            : current - previous;
        break;
      case "pctChangeFromPrevious":
        out[column.key] =
          i === 0 || current === null || !previous
            ? null
            : ((current - previous) / Math.abs(previous)) * 100;
        break;
      case "yearToDate": {
        const columnYear = yearOfKey(column.path[column.path.length - 1]);
        if (columnYear !== year) {
          running = 0;
          year = columnYear;
        }
        running += current ?? 0;
        out[column.key] = current === null && running === 0 ? null : running;
        break;
      }
      case "vsSamePeriodLastYear": {
        const last = column.path[column.path.length - 1];
        const prior = priorYearKey(last);
        const priorKey = prior
          ? joinKey([...column.path.slice(0, -1), prior])
          : null;
        const priorValue = priorKey
          ? (cells[priorKey]?.[value.id] ?? null)
          : null;
        out[column.key] =
          current === null || !priorValue
            ? null
            : ((current - priorValue) / Math.abs(priorValue)) * 100;
        break;
      }
      default:
        break;
    }
  });
  return out;
};

const transformCells = (
  cells: Cells,
  result: PivotResult,
  value: PivotValue,
): Cells => {
  const showAs = value.showAs ?? "value";
  const next: Cells = {};
  Object.entries(cells).forEach(([key, cell]) => {
    next[key] = { ...cell };
  });
  const set = (key: string, n: number | null) => {
    next[key] = { ...(next[key] ?? {}), [value.id]: n };
  };

  switch (showAs) {
    case "pctOfRow":
      Object.keys(cells).forEach((key) =>
        set(key, ratio(cells[key][value.id], cells[TOTAL_KEY]?.[value.id])),
      );
      break;
    case "pctOfColumn":
      Object.keys(cells).forEach((key) =>
        set(
          key,
          ratio(cells[key][value.id], result.grandTotal[key]?.[value.id]),
        ),
      );
      break;
    case "pctOfTotal":
      Object.keys(cells).forEach((key) =>
        set(
          key,
          ratio(cells[key][value.id], result.grandTotal[TOTAL_KEY]?.[value.id]),
        ),
      );
      break;
    case "diffFromPrevious":
    case "pctChangeFromPrevious":
    case "yearToDate":
    case "vsSamePeriodLastYear": {
      const across = acrossColumns(cells, result, value, showAs);
      Object.keys(cells).forEach((key) => {
        if (!(key in across)) set(key, null);
      });
      Object.entries(across).forEach(([key, n]) => set(key, n));
      break;
    }
    default:
      break;
  }
  return next;
};

const applyWithinSiblings = (
  rows: PivotRowNode[],
  value: PivotValue,
  columnKeys: string[],
): PivotRowNode[] => {
  const showAs = value.showAs;
  const running: Record<string, number> = {};
  const ranks: Record<string, Map<PivotRowNode, number>> = {};
  if (showAs === "rank") {
    columnKeys.forEach((columnKey) => {
      const ranked = rows.filter(
        (row) => !row.isOther && row.cells[columnKey]?.[value.id] != null,
      );
      // Competition ranking (1, 2, 2, 4): one sort, then ties share the first position
      const sorted = ranked
        .map((row) => {
          return { row, n: row.cells[columnKey][value.id] ?? 0 };
        })
        .sort((a, b) => b.n - a.n);
      const rankByRow = new Map<PivotRowNode, number>();
      sorted.forEach(({ row, n }, i) => {
        const previous = sorted[i - 1];
        rankByRow.set(
          row,
          previous && previous.n === n ? rankByRow.get(previous.row)! : i + 1,
        );
      });
      ranks[columnKey] = rankByRow;
    });
  }
  return rows.map((row) => {
    const cells: Cells = {};
    columnKeys.forEach((columnKey) => {
      const raw = row.cells[columnKey]?.[value.id] ?? null;
      let n: number | null = raw;
      if (showAs === "runningTotal") {
        running[columnKey] = (running[columnKey] ?? 0) + (raw ?? 0);
        n = raw === null ? null : running[columnKey];
      } else if (showAs === "rank") {
        n = ranks[columnKey].get(row) ?? null;
      }
      cells[columnKey] = { ...(row.cells[columnKey] ?? {}), [value.id]: n };
    });
    return {
      ...row,
      cells: { ...row.cells, ...cells },
      children: row.children
        ? applyWithinSiblings(row.children, value, columnKeys)
        : undefined,
    };
  });
};

const mapRows = (
  rows: PivotRowNode[],
  fn: (row: PivotRowNode) => PivotRowNode,
): PivotRowNode[] =>
  rows.map((row) => {
    const mapped = fn(row);
    return mapped.children
      ? { ...mapped, children: mapRows(mapped.children, fn) }
      : mapped;
  });

/** Comparisons across columns need a column field; year comparisons need it grouped by date */
export const showAsSupported = (showAs: PivotShowAs, config: PivotConfig) => {
  const lastColumn = config.columns[config.columns.length - 1];
  switch (showAs) {
    case "diffFromPrevious":
    case "pctChangeFromPrevious":
      return !!lastColumn;
    case "yearToDate":
    case "vsSamePeriodLastYear":
      return !!lastColumn?.dateGrouping;
    default:
      return true;
  }
};

/** Returns a copy of `result` with each value converted by its `showAs` */
export const applyShowAs = (
  result: PivotResult,
  config: PivotConfig,
): PivotResult => {
  let rows = result.rows;
  let grandTotal = result.grandTotal;
  const columnKeys = Array.from(
    new Set([...result.columnKeys.map((column) => column.key), TOTAL_KEY]),
  );
  for (const value of config.values) {
    const showAs = value.showAs ?? "value";
    if (showAs === "value" || !showAsSupported(showAs, config)) continue;
    if (showAs === "runningTotal" || showAs === "rank") {
      rows = applyWithinSiblings(rows, value, columnKeys);
      if (showAs === "rank") {
        grandTotal = Object.fromEntries(
          Object.entries(grandTotal).map(([key, cell]) => [
            key,
            { ...cell, [value.id]: null },
          ]),
        );
      }
      continue;
    }
    rows = mapRows(rows, (row) => {
      return {
        ...row,
        cells: transformCells(row.cells, result, value),
      };
    });
    grandTotal = transformCells(grandTotal, result, value);
  }
  return { ...result, rows, grandTotal };
};

export const SHOW_AS_LABELS: Record<PivotShowAs, string> = {
  value: "Value",
  pctOfRow: "% of row",
  pctOfColumn: "% of column",
  pctOfTotal: "% of grand total",
  runningTotal: "Running total",
  rank: "Rank",
  diffFromPrevious: "Difference from previous",
  pctChangeFromPrevious: "% change from previous",
  yearToDate: "Year to date",
  vsSamePeriodLastYear: "% vs same period last year",
};
